"use client";

import { Select } from "@tosize/ui";
import { Fragment } from "react";
import { useQuery } from "~/hooks/useQuery";
import type { GridPerPage } from "../../ProductsGrid";
import { searchParamsSchema } from "../../grid";
import { FilterItemOption } from "../Filter/FilterItemOption";
import { AccordionFilterItem } from "../Filter/FilterOptions";
import { useFilterOption } from "../Filter/useFilterOption";
import type { VariantProps } from "./Options";

interface LimitOptionsProps extends VariantProps {
	title: string;
	gridPerPage: GridPerPage;
	listPerPage: GridPerPage;
}

export const LIMIT_PARAM_NAME = "limit";

export function LimitOptions({ variant, title, gridPerPage, listPerPage }: LimitOptionsProps) {
	const { searchParams, router, createUrl } = useQuery();
	const { mode, limit: limitParam } = searchParamsSchema.parse(
		Object.fromEntries(searchParams.entries()),
	);
	const productsPerPage = mode === "grid" ? gridPerPage : listPerPage;
	const limit = limitParam ?? productsPerPage.default;

	return (
		<Fragment>
			{variant === "stickybar" ? (
				<div className="flex items-center gap-2">
					<span>{title}</span>

					<Select
						size="sm"
						value={limit}
						onChange={(event) => router.push(createUrl({ [LIMIT_PARAM_NAME]: event.target.value }))}
					>
						{productsPerPage.options.map((number) => (
							<option key={number} value={number?.toString()}>
								{number}
							</option>
						))}
					</Select>
				</div>
			) : (
				<AccordionFilterItem value={LIMIT_PARAM_NAME} title={title} className="xl:hidden">
					<div className="grid grid-cols-4 gap-4 pb-4">
						{productsPerPage.options.map((number) => (
							<LimitOption value={number?.toString() ?? ""} defaultValue={limit} key={number} />
						))}
					</div>
				</AccordionFilterItem>
			)}
		</Fragment>
	);
}

function LimitOption({ value, defaultValue }: { value: string; defaultValue?: number }) {
	const { createUrl } = useQuery();
	const { isActive } = useFilterOption({ code: LIMIT_PARAM_NAME, value });

	return (
		<FilterItemOption
			href={createUrl({ [LIMIT_PARAM_NAME]: value })}
			title={value}
			isActive={defaultValue ? defaultValue.toString() === value : isActive}
			mode="grid"
		/>
	);
}
